import React from 'react';
import {observer} from 'mobx-react';
import {withState, t} from 'shared/core';
import {types, endpoints, auth} from 'main/tools';
import {FormattedMessage} from 'react-intl';
import {EmployeeNameWithAvatar, Layout, PanelTooltip} from 'shared/components';
import {InteractiveTable, FancyFilter} from 'main/components';
import {NmbrDirectoryEmployee} from 'main/stores/nmbr';
import AddEmployeeModal from './AddEmployeeModal';
import RemoveEmployeeModal from './RemoveEmployeeModal';
import useModal from 'shared/hooks/useModal';
import {NmbrEmployeesDirectoryState} from '../state';
import BatchAddEmployeesModal from './BatchAddEmployeesModal';
import BatchAddEmployeesResultsModal from './BatchAddEmployeesResultsModal';
import _ from 'lodash';
import {statusTagColour} from 'main/containers/employees/tools';
import ErroredEmployeesWarning from '../../ErroredEmployeesWarning';
import EmployeeErroredModal from './EmployeeErroredModal';
import EmployeeErrors from '../../EmployeeErrors';

const SyncErrors = observer(({nmbrEmployee}) => {
  return (
    <div className='flex align-items-middle'>
      <PanelTooltip icon={<i className='material-icons scarlet mr1 mln1 h2'>{'warning'}</i>}>
        <div className='mb1'><FormattedMessage id='nmbr.employees.directory.The following information is invalid and could not be processed for this employee:'/></div>
        <div className='space-y'>
          <EmployeeErrors nmbrEmployee={nmbrEmployee}/>
        </div>
      </PanelTooltip>
      <FormattedMessage id='nmbr.employees.directory.Errors'/>
    </div>
  );
});

const EmployeeName = observer(({model}) => {
  const tags = model.statusTags.map(status => {
    return {
      content: t(`employees.profile.${status}`),
      colour: statusTagColour(status)
    };
  });

  if (model.terminated && !auth.hasAccess('::TERMINATE_EMPLOYEES')) {
    return <EmployeeNameWithAvatar employee={model} size='sm' tags={tags} />;
  }

  return (
    <EmployeeNameWithAvatar
      href={`/employees/${model.id}/`} employee={model} size='sm' tags={tags}
    />
  );
});

const NmbrStatus = observer(({model}) => {
  if (model.nmbrPayrollEnabled && !_.isEmpty(model.errorsFromLastSync)) {
    return <SyncErrors nmbrEmployee={model}/>;
  } else if (model.nmbrPayrollEnabled) {
    return (
      <div className='flex align-items-middle'>
        <i className='material-icons grass mr1 h2'>{'done'}</i>
        <FormattedMessage id='nmbr.employees.directory.In Payroll'/>
      </div>
    );
  }

  return (
    <div className='flex align-items-middle'>
      <i className='material-icons waterloo mr1 h2'>{'horizontal_rule'}</i>
      <FormattedMessage id='nmbr.employees.directory.Not In Payroll'/>
    </div>
  );
});

const COLUMNS = [
  {
    header: 'nmbr.employees.directory.Employee',
    component: EmployeeName,
    width: 3,
    sortBy: 'name'
  },
  {
    header: 'nmbr.employees.directory.Department',
    width: 2,
    attribute: 'department.name'
  },
  {
    header: 'nmbr.employees.directory.Location',
    width: 2,
    attribute: 'location.name'
  },
  {
    header: 'nmbr.employees.directory.Employment Type',
    width: 2,
    render: model => <FormattedMessage id={`nmbr.employees.directory.employment_type.${model.employmentType}`}/>
  },
  {
    header: 'nmbr.employees.directory.Status',
    component: NmbrStatus,
    width: 2
  }
];

const LOCALIZATION = {
  interactive: {
    addModel: 'nmbr.employees.directory.Batch Add Employees',
  },
  emptyState: 'nmbr.employees.directory.No employees to display'
};

const NmbrEmployeesDirectoryWithState = observer(({erroredNmbrEmployees, uiState}) => {
  const addEmployeeModalOpen = useModal(uiState, 'AddEmployeeModal', 'selectedEmployee', null, 'errors');
  const removeEmployeeModalOpen = useModal(uiState, 'RemoveEmployeeModal', 'selectedEmployee', null, 'errors');
  const batchAddEmployeesModalOpen = useModal(uiState, 'BatchAddEmployeesModal');
  const batchAddEmployeesResultsModalOpen = useModal(uiState, 'BatchAddEmployeesResultsModal');
  const employeeErroredModalOpen = useModal(uiState, 'EmployeeErroredModal', 'selectedEmployee', NmbrDirectoryEmployee);

  return (
    <Layout>
      <ErroredEmployeesWarning erroredNmbrEmployees={erroredNmbrEmployees}/>
      <InteractiveTable
        title={t('nmbr.employees.directory.Employees')}
        onAdd={() => uiState.openBatchAddEmployeesModal()}
        uiState={uiState}
        columns={COLUMNS}
        localization={LOCALIZATION}
        saveStateToUrl
        fancyFiltersEnabled
        initialFilter={{employment_status: 'non_terminated'}}
        onMount={agent => uiState.setInteractiveAgent(agent)}
        customLinks={model => uiState.customLinksFor(model)}
        proxy={{
          type: 'async',
          modelType: types.EMPLOYEE,
          model: NmbrDirectoryEmployee,
          endpoint: endpoints.NMBR.EMPLOYEES.ALL
        }}
      >
        <FancyFilter
          filters={[
            'location',
            'department',
            'employment_type',
            'employment_status'
          ]}
        />
      </InteractiveTable>
      <AddEmployeeModal uiState={uiState} modalOpen={addEmployeeModalOpen}/>
      <RemoveEmployeeModal uiState={uiState} modalOpen={removeEmployeeModalOpen}/>
      <BatchAddEmployeesModal uiState={uiState} modalOpen={batchAddEmployeesModalOpen}/>
      <BatchAddEmployeesResultsModal uiState={uiState} modalOpen={batchAddEmployeesResultsModalOpen}/>
      <EmployeeErroredModal uiState={uiState} modalOpen={employeeErroredModalOpen}/>
    </Layout>
  );
});

export default withState(NmbrEmployeesDirectoryWithState, NmbrEmployeesDirectoryState);
